import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  BadgePoundSterling,
  Bell,
  BookOpenText,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  CreditCard,
  Globe2,
  HandCoins,
  Handshake,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Lock,
  LogOut,
  Mail,
  MessageSquareText,
  Monitor,
  Moon,
  Newspaper,
  PhoneCall,
  ReceiptText,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Sun,
  Target,
  TicketCheck,
  UserCog,
  UserRound,
  Users,
  WalletCards,
  Workflow,
} from 'lucide-react';
import { api } from '../lib/api';
import { getThemePreference, setThemePreference } from '../lib/theme';
import WorkspaceZoom from './WorkspaceZoom';
import { isNativeDesktopApp, returnToDesktopLogin } from '../lib/desktop';
import DropdownMenu from './DropdownMenu';
import NotificationCentre from './NotificationCentre';

const sections=[
  {label:null,items:[{to:'/',label:'Home',icon:LayoutDashboard}]},
  {label:'Customers',items:[{to:'/customers',label:'Customers',icon:UserRound},{to:'/organisations',label:'Organisations',icon:Building2},{to:'/service-invitations',label:'Service invitations',icon:Send}]},
  {label:'Communications',items:[{to:'/inbox',label:'Inbox',icon:Mail},{to:'/whatsapp-inbox',label:'WhatsApp inbox',icon:MessageSquareText},{to:'/communications/templates',label:'Template library',icon:BookOpenText},{to:'/communications/publications',label:'Newsletters and magazine',icon:Newspaper}]},
  {label:'Sales',items:[{to:'/leads',label:'Leads',icon:Target},{to:'/opportunities',label:'Opportunities',icon:Handshake},{to:'/pipeline',label:'Pipelines',icon:Workflow},{to:'/sales',label:'Sales',icon:ReceiptText}]},
  {label:'Case management',items:[{to:'/cases',label:'Cases',icon:TicketCheck},{to:'/integrity',label:'Integrity reports',icon:ShieldCheck},{to:'/complaints',label:'Complaints',icon:MessageSquareText},{to:'/data-protection',label:'Data protection',icon:ShieldCheck},{to:'/calls',label:'Calls',icon:PhoneCall},{to:'/back-office',label:'Back Office',icon:UserCog}]},
  {label:'Services',items:[{to:'/profiles',label:'Sousa Murray Profiles',icon:UserRound},{to:'/mini-websites',label:'Sousa Murray Mini Websites',icon:Globe2}]},
  {label:'Work',items:[{to:'/tasks',label:'Tasks',icon:ClipboardList},{to:'/meetings',label:'Meetings',icon:CalendarDays}]},
  {label:'Commercial',items:[{to:'/billing',label:'Billing',icon:BadgePoundSterling},{to:'/collections',label:'Collections and recoveries',icon:HandCoins},{to:'/studio',label:'Studio plans and controls',icon:Globe2}]},
  {label:'Administration',items:[{to:'/finance',label:'Finance and Stripe',icon:Landmark},{to:'/stripe-operations',label:'Stripe operations',icon:CreditCard},{to:'/paypal-operations',label:'PayPal operations',icon:WalletCards},{to:'/profiles/admin',label:'Profiles Admin Centre',icon:ShieldCheck},{to:'/partnerships',label:'Partnerships and third parties',icon:Handshake},{to:'/identity',label:'Sousa Murray ID',icon:Lock},{to:'/microsoft365',label:'Microsoft 365',icon:Mail},{to:'/staff-directory',label:'Staff directory',icon:Users},{to:'/company-id',label:'Company ID',icon:BadgeCheck},{to:'/whatsapp',label:'WhatsApp settings',icon:Settings2},{to:'/system-configuration',label:'Secrets and variables',icon:KeyRound},{to:'/website-seos',label:'Website SEOs',icon:Globe2},{to:'/data-integrity',label:'Customer data integrity',icon:ShieldCheck},{to:'/settings',label:'CRM settings',icon:Settings2}]},
];

const primaryNavigation=[
  {to:'/',label:'Home'},
  {to:'/customers',label:'Customers'},
  {to:'/cases',label:'Case management'},
  {to:'/inbox',label:'Communications'},
  {to:'/data-protection',label:'Data protection'},
  {to:'/billing',label:'Billing'},
  {to:'/settings',label:'Settings'},
];

export default function AppShell(){
  const[query,setQuery]=useState(''),[results,setResults]=useState([]),[me,setMe]=useState(null),[authError,setAuthError]=useState(''),[themeMode,setThemeMode]=useState(()=>getThemePreference()),[servicesOpen,setServicesOpen]=useState(false);
  const navigate=useNavigate();
  const location=useLocation();

  useEffect(()=>{setServicesOpen(false);},[location.pathname]);
  useEffect(()=>{api('/api/me').then(setMe).catch(error=>setAuthError(error.message));},[]);
  useEffect(()=>{
    const value=query.trim();
    if(value.length<2){setResults([]);return;}
    const controller=new AbortController();
    const timer=setTimeout(()=>{
      api(`/api/search?q=${encodeURIComponent(value)}`,{signal:controller.signal}).then(data=>setResults(data.results||[])).catch(()=>setResults([]));
    },220);
    return()=>{clearTimeout(timer);controller.abort();};
  },[query]);
  useEffect(()=>{
    const changed=event=>setThemeMode(event.detail?.preference||getThemePreference());
    window.addEventListener('crm-theme-change',changed);
    return()=>window.removeEventListener('crm-theme-change',changed);
  },[]);

  const chooseTheme=mode=>{setThemePreference(mode);setThemeMode(mode);};
  const signOut=async()=>{
    if(isNativeDesktopApp()){
      try{if(await returnToDesktopLogin())return;}catch{}
    }
    window.location.assign('/cdn-cgi/access/logout');
  };
  const openResult=result=>{
    const route=result.record_type==='customer'?`/customers/${result.id}`:result.record_type==='lead'?'/leads':result.record_type==='deal'?'/opportunities':result.record_type==='organisation'?'/organisations':['shop_order','shop_quote'].includes(result.record_type)?`/orders/${encodeURIComponent(result.id||result.reference||'')}`:'/';
    navigate(route);
    setQuery('');
  };

  const display=me?.staff?.display_name||me?.staff?.email||'Staff';
  const initials=display.split(/\s+/).slice(0,2).map(value=>value[0]).join('').toUpperCase();
  const roleName=authError?'Access not linked':me?.staff?.job_title||me?.roles?.[0]?.name||'Staff';
  const profileHeader=<div className="staff-dropdown-profile"><span className="avatar large">{initials||'SM'}</span><span><strong>{display}</strong><small>{me?.staff?.email||roleName}</small></span></div>;
  const profileGroups=[
    {label:'Account',items:[
      {label:'Profile settings',icon:UserRound,onSelect:()=>navigate('/staff-directory')},
      {label:'My Company ID',icon:BadgeCheck,onSelect:()=>navigate('/my-company-id')},
      {label:'Email preferences',icon:Mail,onSelect:()=>navigate('/settings#email-preferences')},
      {label:'Notifications',icon:Bell,onSelect:()=>navigate('/settings#notifications')},
      {label:'Privacy and security',icon:Lock,onSelect:()=>navigate('/settings#security')},
    ]},
    {label:'Appearance',items:[
      {key:'appearance-light',label:'Light',icon:Sun,selected:themeMode==='light',onSelect:()=>chooseTheme('light')},
      {key:'appearance-dark',label:'Dark',icon:Moon,selected:themeMode==='dark',onSelect:()=>chooseTheme('dark')},
      {key:'appearance-system',label:'System',icon:Monitor,selected:themeMode==='system',onSelect:()=>chooseTheme('system')},
    ]},
    {label:'Customer Operations',items:[
      {label:'Inbox',icon:Mail,onSelect:()=>navigate('/inbox')},
      {label:'WhatsApp inbox',icon:MessageSquareText,onSelect:()=>navigate('/whatsapp-inbox')},
      {label:'Cases',icon:TicketCheck,onSelect:()=>navigate('/cases')},
      {label:'Data protection',icon:ShieldCheck,onSelect:()=>navigate('/data-protection')},
      {label:'Template library',icon:BookOpenText,onSelect:()=>navigate('/communications/templates')},
      {label:'Collections and recoveries',icon:HandCoins,onSelect:()=>navigate('/collections')},
      {label:'Back Office',icon:UserCog,onSelect:()=>navigate('/back-office')},
      {label:'Finance and Stripe',icon:Landmark,onSelect:()=>navigate('/finance')},
      {label:'Stripe operations',icon:CreditCard,onSelect:()=>navigate('/stripe-operations')},
      {label:'PayPal operations',icon:WalletCards,onSelect:()=>navigate('/paypal-operations')},
      {label:'Partnerships and third parties',icon:Handshake,onSelect:()=>navigate('/partnerships')},
      {label:'Sousa Murray ID',icon:Lock,onSelect:()=>navigate('/identity')},
      {label:'Microsoft 365',icon:Mail,onSelect:()=>navigate('/microsoft365')},
      {label:'Company ID',icon:BadgeCheck,onSelect:()=>navigate('/company-id')},
      {label:'Secrets and variables',icon:KeyRound,onSelect:()=>navigate('/system-configuration')},
      {label:'Roles and permissions',icon:Users,onSelect:()=>navigate('/settings#roles')},
      {label:'CRM settings',icon:Settings2,onSelect:()=>navigate('/settings')},
    ]},
    {items:[{label:'Sign out',icon:LogOut,danger:true,onSelect:()=>void signOut()}]},
  ];

  return <div className="app-shell sm-operations-shell">
    <a href="#main-content" className="govuk-skip-link">Skip to main content</a>
    <header className="sm-operations-header">
      <div className="govuk-generic-header sm-operations-generic-header">
        <div className="govuk-generic-header__container govuk-width-container sm-operations-header-inner">
          <div className="govuk-generic-header__logo">
            <NavLink className="govuk-generic-header__homepage-link sm-operations-brand" to="/" aria-label="Sousa Murray Customer Operations home">
              <img src="/sousa-murray-icon.svg" width="36" height="36" alt="" aria-hidden="true"/>
              <span><strong>SOUSA MURRAY</strong><small>Customer Operations</small></span>
            </NavLink>
          </div>
          <div className="sm-operations-header-actions">
            <span className="sm-operations-private-label">Internal staff service</span>
            <NotificationCentre staff={me?.staff}/>
            <DropdownMenu ariaLabel="Staff account" trigger={<><span className="avatar">{initials||'SM'}</span><span className="profile-copy"><strong>{display}</strong><small>{roleName}</small></span><ChevronDown size={16}/></>} triggerClassName="profile-button profile-menu-trigger" header={profileHeader} groups={profileGroups} width={270}/>
          </div>
        </div>
      </div>
      <nav className="govuk-service-navigation sm-operations-service-navigation" aria-label="Primary navigation">
        <div className="govuk-width-container sm-operations-service-navigation__inner">
          <div className="govuk-service-navigation__container">
            <span className="govuk-service-navigation__service-name"><NavLink to="/" className="govuk-service-navigation__link">Customer Operations</NavLink></span>
            <div className="govuk-service-navigation__wrapper">
              <ul className="govuk-service-navigation__list sm-operations-primary-links">
                {primaryNavigation.map(item=><li key={item.to} className="govuk-service-navigation__item"><NavLink end={item.to==='/'} to={item.to} className={({isActive})=>`govuk-service-navigation__link sm-operations-primary-link${isActive?' sm-operations-primary-link--current':''}`}>{item.label}</NavLink></li>)}
              </ul>
            </div>
            <details className="sm-operations-all-services" open={servicesOpen} onToggle={event=>setServicesOpen(event.currentTarget.open)} onKeyDown={event=>{
              if(event.key==='Escape'&&event.currentTarget.open){
                event.preventDefault();
                setServicesOpen(false);
                event.currentTarget.querySelector('summary')?.focus();
              }
            }}>
              <summary>All services</summary>
              <div className="sm-operations-service-drawer">
                <div className="sm-operations-service-drawer__heading"><h2 className="govuk-heading-m">All Customer Operations services</h2><p className="govuk-body-s">Choose a service area to continue.</p></div>
                <nav aria-label="All Customer Operations services" className="sm-operations-service-groups">
                  {sections.map((section,index)=><section className="sm-operations-service-group" key={section.label||index}>
                    <h3 className="govuk-heading-s">{section.label||'Overview'}</h3>
                    <ul className="govuk-list">{section.items.map(({to,label})=><li key={to}><NavLink end={to==='/'} to={to} className="govuk-link" onClick={()=>setServicesOpen(false)}>{label}</NavLink></li>)}</ul>
                  </section>)}
                </nav>
              </div>
            </details>
          </div>
        </div>
      </nav>
    </header>
    <div className="govuk-width-container sm-operations-width-container">
      <div className="sm-operations-utilitybar">
        <div className="sm-operations-search">
          <label className="govuk-label govuk-label--s" htmlFor="global-record-search">Search records</label>
          <div className="sm-operations-search__control global-search-wrap">
            <Search size={18} aria-hidden="true"/>
            <input id="global-record-search" className="govuk-input" value={query} onChange={event=>setQuery(event.target.value)} placeholder="Customer, case or reference" autoComplete="off"/>
            {query.trim().length>=2&&<div className="search-results" aria-live="polite">{results.length?results.map(result=><button key={result.record_type+'-'+result.id} type="button" onClick={()=>openResult(result)}><span className="result-type">{result.record_type}</span><strong>{result.title}</strong><small>{result.reference} · {result.subtitle||result.status||''}</small></button>):<div className="search-empty">No matching records</div>}</div>}
          </div>
        </div>
        <WorkspaceZoom/>
      </div>
      <div className="main-column"><main className="content" id="main-content" tabIndex={-1}><Outlet/></main></div>
    </div>
    <footer className="sm-operations-footer">
      <div className="govuk-width-container sm-operations-footer__inner">
        <div><strong>Sousa Murray Group Ltd</strong><p className="govuk-body-s">Customer Operations · Internal staff service</p></div>
        <p className="govuk-body-s">For authorised staff only. Access and actions may be audited.</p>
      </div>
    </footer>
  </div>;
}
